import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import { Alert, Avatar, Box, Button, Card, Chip, Collapse, Divider, FormControl, Grid, MenuItem, Select, Snackbar, TextField, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { City, Country, State } from 'country-state-city';
import { parseISO } from 'date-fns';
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../contexts/auth';
import { getUserId, updateUserId } from '../../services/api';

const ROLE_CONFIG = {
  super:   { label: 'Super Admin',   bg: '#4527a0', color: '#fff' },
  admin:   { label: 'Administrador', bg: '#1565c0', color: '#fff' },
  visitor: { label: 'Visitante',     bg: '#e65100', color: '#fff' },
};

const Section = ({ title, children, hideDivider }) => (
  <>
    <Box sx={{ px: 4, py: 3 }}>
      {title && (
        <Typography variant="overline" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 2, letterSpacing: 1 }}>
          {title}
        </Typography>
      )}
      {children}
    </Box>
    {!hideDivider && <Divider />}
  </>
);

export const Profile = () => {
  const { id } = useContext(AuthContext);

  const [user, setUser] = useState(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');

  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState({});

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleCancelPassword = () => {
    setChangingPassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordErrors({});
  };

  const validatePasswordFields = () => {
    const errs = {};
    if (!currentPassword) errs.currentPassword = 'Senha atual obrigatória';
    if (!newPassword) errs.newPassword = 'Senha obrigatório';
    else if (newPassword.length > 18) errs.newPassword = 'Senha muito longa! Sua senha deve conter entre 8 e 18 caracteres';
    else if (newPassword.length < 8) errs.newPassword = 'Senha muito curta! Sua senha deve conter entre 8 e 18 caracteres';
    if (!confirmPassword) errs.confirmPassword = 'Confirmar senha obrigatório';
    else if (confirmPassword !== newPassword) errs.confirmPassword = 'As senhas devem ser igual';
    return errs;
  };

  const loadUser = () => {
    if (!id) return;
    getUserId(id).then(({ data }) => {
      setUser(data);
      setFullName(data.fullName ?? '');
      setEmail(data.email ?? '');
      setBirthDate(data.birthDate ? parseISO(data.birthDate) : null);
      const countryObj = Country.getAllCountries().find((c) => c.name === data.country);
      const stateObj = countryObj
        ? State.getStatesOfCountry(countryObj.isoCode).find((s) => s.name === data.state)
        : null;
      const cityObj = stateObj
        ? City.getCitiesOfState(countryObj.isoCode, stateObj.isoCode).find((c) => c.name === data.city)
        : null;
      setCountry(countryObj || '');
      setState(stateObj || '');
      setCity(cityObj || '');
    }).catch(() => {});
  };

  useEffect(() => { loadUser(); }, [id]);

  const handleSave = async () => {
    if (changingPassword) {
      const errs = validatePasswordFields();
      if (Object.keys(errs).length > 0) {
        setPasswordErrors(errs);
        return;
      }
    }

    const data = {
      fullName,
      email,
      birthDate,
      country: country?.name || country,
      state: state?.name || state,
      city: city?.name || city,
      role: user?.role,
      ...(changingPassword && { currentPassword, password: newPassword }),
    };

    try {
      await updateUserId(id, data);
      showSnackbar('Perfil atualizado com sucesso!');
      handleCancelPassword();
      loadUser();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Erro ao atualizar perfil.';
      showSnackbar(msg, 'error');
    }
  };

  const roleConfig = user ? ROLE_CONFIG[user.role] : null;

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 5, px: 3, pb: 5 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Meu Perfil
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        Gerencie suas informações pessoais e configurações de conta
      </Typography>

      <Card variant="outlined" sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Section>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: '#4a9e8e' }}>
              <PersonIcon sx={{ fontSize: 36, color: '#fff' }} />
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={700}>
                  {user?.fullName ?? '—'}
                </Typography>
                {roleConfig && (
                  <Chip
                    label={roleConfig.label}
                    size="small"
                    sx={{ bgcolor: roleConfig.bg, color: roleConfig.color, fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {user?.email ?? '—'}
              </Typography>
            </Box>
          </Box>
        </Section>

        <Section>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonIcon sx={{ color: '#4a9e8e' }} />
            <Typography variant="subtitle1" fontWeight={700}>Informações Pessoais</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome completo"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  label="Data de nascimento"
                  inputFormat="DD/MM/YYYY"
                  value={birthDate}
                  onChange={(val) => setBirthDate(val)}
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Section>

        <Section>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <EmailIcon sx={{ color: '#4a9e8e' }} />
            <Typography variant="subtitle1" fontWeight={700}>Contato</Typography>
          </Box>
          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
          />
        </Section>

        <Section>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocationOnIcon sx={{ color: '#4a9e8e' }} />
            <Typography variant="subtitle1" fontWeight={700}>Localização</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>País</Typography>
              <FormControl fullWidth size="small">
                <Select value={country} displayEmpty onChange={(e) => { setCountry(e.target.value); setState(''); setCity(''); }}>
                  <MenuItem value=""><em>Selecione</em></MenuItem>
                  {Country.getAllCountries().map((c) => (
                    <MenuItem key={c.isoCode} value={c}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>Estado</Typography>
              <FormControl fullWidth size="small">
                <Select value={state} displayEmpty disabled={!country} onChange={(e) => { setState(e.target.value); setCity(''); }}>
                  <MenuItem value=""><em>Selecione</em></MenuItem>
                  {State.getStatesOfCountry(country?.isoCode).map((s) => (
                    <MenuItem key={s.isoCode} value={s}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>Cidade</Typography>
              <FormControl fullWidth size="small">
                <Select value={city} displayEmpty disabled={!state} onChange={(e) => setCity(e.target.value)}>
                  <MenuItem value=""><em>Selecione</em></MenuItem>
                  {City.getCitiesOfState(country?.isoCode, state?.isoCode).map((c) => (
                    <MenuItem key={c.name} value={c}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Section>

        <Section>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LockIcon sx={{ color: '#4a9e8e', mr: 1 }} />
            <Typography variant="subtitle1" fontWeight={700}>Segurança</Typography>
            {!changingPassword && (
              <Typography
                variant="body2"
                onClick={() => setChangingPassword(true)}
                sx={{ ml: 'auto', color: '#4a9e8e', cursor: 'pointer', '&:hover': { color: '#3d8a7c' } }}
              >
                Alterar senha
              </Typography>
            )}
          </Box>
          <Collapse in={changingPassword}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Senha atual"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => { setCurrentPassword(e.target.value); setPasswordErrors((p) => ({ ...p, currentPassword: undefined })); }}
                  fullWidth
                  size="small"
                  placeholder="Digite sua senha atual"
                  {...(passwordErrors.currentPassword && { error: true, helperText: passwordErrors.currentPassword })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nova senha"
                  type="password"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setPasswordErrors((p) => ({ ...p, newPassword: undefined })); }}
                  fullWidth
                  size="small"
                  placeholder="Entre 8 e 18 caracteres"
                  {...(passwordErrors.newPassword && { error: true, helperText: passwordErrors.newPassword })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Confirmar nova senha"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setPasswordErrors((p) => ({ ...p, confirmPassword: undefined })); }}
                  fullWidth
                  size="small"
                  placeholder="Repita a nova senha"
                  {...(passwordErrors.confirmPassword && { error: true, helperText: passwordErrors.confirmPassword })}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  onClick={handleCancelPassword}
                  sx={{ color: '#4a9e8e', cursor: 'pointer', '&:hover': { color: '#3d8a7c' } }}
                >
                  Cancelar alteração de senha
                </Typography>
              </Grid>
            </Grid>
          </Collapse>
        </Section>

        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{ bgcolor: '#4a9e8e', color: '#fff', textTransform: 'none', fontWeight: 600, borderRadius: 2, '&:hover': { bgcolor: '#3d8a7c' } }}
          >
            Salvar Alterações
          </Button>
        </Box>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
